import { motion } from "framer-motion";
import { useSettings } from "../contexts/SettingsContext";
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { EXTERNAL_LINKS } from "../constants/links";

export default function Partners() {
  const { settings } = useSettings();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const benefits = [
    {
      icon: BuildingOfficeIcon,
      title: "Enterprise Integration",
      description:
        "Seamlessly integrate our DeFi automation tools into your existing infrastructure and workflows.",
    },
    {
      icon: ChartBarIcon,
      title: "Revenue Sharing",
      description:
        "Earn competitive revenue shares while providing cutting-edge DeFi solutions to your users.",
    },
    {
      icon: ShieldCheckIcon,
      title: "Security & Compliance",
      description:
        "Built with enterprise-grade security and regulatory compliance to meet your standards.",
    },
    {
      icon: RocketLaunchIcon,
      title: "Go-to-Market Support",
      description:
        "Get comprehensive marketing support, co-branded materials, and joint go-to-market strategies.",
    },
    {
      icon: CurrencyDollarIcon,
      title: "Flexible Pricing",
      description:
        "Choose from various partnership models that align with your business goals and scale.",
    },
    {
      icon: UserGroupIcon,
      title: "Dedicated Support",
      description:
        "Access to our dedicated partner success team for technical support and business development.",
    },
  ];

  return (
    <section id="partners" className="py-24 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent" />
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-gradient-to-r from-blue-400/10 to-purple-400/10 blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-gradient-to-r from-green-400/10 to-blue-400/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 dark:bg-white/10 backdrop-blur-sm border border-gray-200/50 dark:border-white/20 mb-6"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <UserGroupIcon
                className="h-5 w-5"
                style={{ color: settings.primaryColor }}
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Partnership Program
              </span>
            </motion.div>

            <header className="text-center mb-16">
              <motion.h2
                variants={itemVariants}
                className="text-3xl sm:text-4xl lg:text-5xl font-oxanium font-bold mb-6"
                style={{ color: settings.primaryColor }}
              >
                {settings.partnersTitle}
              </motion.h2>
              <motion.p
                variants={itemVariants}
                className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed"
              >
                {settings.partnersSubtitle}
              </motion.p>
            </header>
          </motion.div>

          {/* Benefits Grid */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="group relative p-8 rounded-2xl bg-white/60 dark:bg-white/5 backdrop-blur-sm border border-gray-200/50 dark:border-white/10 hover:border-gray-300/50 dark:hover:border-white/20 transition-all duration-300"
                whileHover={{
                  y: -5,
                  transition: { duration: 0.2 },
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: `${settings.primaryColor}20` }}
                  >
                    <benefit.icon
                      className="h-6 w-6"
                      style={{ color: settings.primaryColor }}
                    />
                  </div>
                  <h3 className="text-xl font-oxanium font-semibold text-gray-900 dark:text-white">
                    {benefit.title}
                  </h3>
                </div>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* Partnership Types */}
          <motion.div variants={itemVariants} className="mb-16">
            <h3 className="text-3xl font-oxanium font-bold text-center text-gray-900 dark:text-white mb-12">
              Partnership Types
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Technology Partners",
                  description: "Integrate our APIs and SDKs into your platform",
                  features: [
                    "API Access",
                    "SDK Integration",
                    "Technical Support",
                    "Co-development",
                  ],
                },
                {
                  title: "Channel Partners",
                  description: "Resell our solutions to your customer base",
                  features: [
                    "Reseller Program",
                    "Marketing Support",
                    "Training",
                    "Revenue Sharing",
                  ],
                },
                {
                  title: "Strategic Partners",
                  description: "Deep integration and joint go-to-market",
                  features: [
                    "Joint Ventures",
                    "Co-branding",
                    "Market Expansion",
                    "Priority Support",
                  ],
                },
              ].map((type, index) => (
                <motion.div
                  key={type.title}
                  className="p-8 rounded-2xl bg-white/40 dark:bg-white/5 backdrop-blur-sm border border-gray-200/50 dark:border-white/10"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <h4 className="text-xl font-oxanium font-semibold text-gray-900 dark:text-white mb-3">
                    {type.title}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    {type.description}
                  </p>
                  <ul className="space-y-2">
                    {type.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                      >
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{ backgroundColor: settings.primaryColor }}
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            variants={itemVariants}
            className="text-center p-12 rounded-3xl bg-gradient-to-br from-white/60 to-white/30 dark:from-white/10 dark:to-white/5 backdrop-blur-sm border border-gray-200/50 dark:border-white/10"
          >
            <h3 className="text-3xl font-oxanium font-bold text-gray-900 dark:text-white mb-4">
              Ready to Partner with Us?
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can work together to revolutionize DeFi
              automation and create mutual value for our businesses.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.a
                href={EXTERNAL_LINKS.telegramPartnership}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-xl font-oxanium font-semibold text-white transition-all duration-300 flex items-center gap-2"
                style={{
                  backgroundColor: settings.primaryColor,
                  boxShadow: `0 4px 14px 0 ${settings.primaryColor}25`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = `0 6px 20px 0 ${settings.primaryColor}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = `0 4px 14px 0 ${settings.primaryColor}25`;
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <UserGroupIcon className="h-5 w-5" />
                Start Partnership Discussion
              </motion.a>

              <motion.a
                href={EXTERNAL_LINKS.documentation}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 rounded-xl font-oxanium font-semibold text-gray-700 dark:text-gray-300 border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Learn More
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
